/**
 * How a campaign's times read on screen. Campaigns run on Eastern time, so every
 * date, time and "today / tomorrow" here is Eastern, whatever the viewer's zone.
 */
import { instantToEasternWallClock } from './eastern-time.js';
import {
  campaignStatus,
  TicketSalesCampaignStatus,
  type CampaignSchedule,
} from './ticket-sales-campaign.js';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DAY_MS = 86_400_000;

interface EasternParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
}

const isValidInstant = (instant: Date | string) => !Number.isNaN(new Date(instant).getTime());

function easternParts(instant: Date | string): EasternParts {
  const [year, month, day, hour, minute] = instantToEasternWallClock(instant)
    .split(/[-T:]/)
    .map(Number);
  return { year, month, day, hour, minute };
}

/** Calendar days from the Eastern date of `from` to the Eastern date of `to`. */
function easternDayDiff(from: Date | string, to: Date | string): number {
  const dayOf = (p: EasternParts) => Date.UTC(p.year, p.month - 1, p.day);
  return Math.round((dayOf(easternParts(to)) - dayOf(easternParts(from))) / DAY_MS);
}

/** "Fri, Oct 30, 2026" — the Eastern calendar date of an instant; blank for an invalid one. */
export function formatCampaignDate(instant: Date | string): string {
  if (!isValidInstant(instant)) return '';
  const { year, month, day } = easternParts(instant);
  const weekday = WEEKDAYS[new Date(Date.UTC(year, month - 1, day)).getUTCDay()];
  return `${weekday}, ${MONTHS[month - 1]} ${day}, ${year}`;
}

/** "12:00 AM" — the Eastern clock time of an instant; blank for an invalid one. */
export function formatCampaignTime(instant: Date | string): string {
  if (!isValidInstant(instant)) return '';
  const { hour, minute } = easternParts(instant);
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${hour12}:${String(minute).padStart(2, '0')} ${hour < 12 ? 'AM' : 'PM'}`;
}

function when(instant: Date | string, verb: string, now: Date): string {
  const days = easternDayDiff(now, instant);
  if (days === 0) return `${verb} today at ${formatCampaignTime(instant)}`;
  if (days === 1) return `${verb} tomorrow`;
  if (days < 0) {
    const { month, day } = easternParts(instant);
    return `${verb} ${MONTHS[month - 1]} ${day}`;
  }
  return `${verb} in ${days} days`;
}

/**
 * The one-line "when" for a campaign: "Ends today at 9:00 PM", "Starts
 * tomorrow", "Ends in 3 days", "Ended Oct 12", or "Hidden from customers"
 * for a disabled one. Blank when either time is not a valid date.
 */
export function campaignRelativeText(campaign: CampaignSchedule, now: Date): string {
  if (!isValidInstant(campaign.startsAt) || !isValidInstant(campaign.endsAt)) return '';
  switch (campaignStatus(campaign, now)) {
    case TicketSalesCampaignStatus.LIVE:
      return when(campaign.endsAt, 'Ends', now);
    case TicketSalesCampaignStatus.SCHEDULED:
      return when(campaign.startsAt, 'Starts', now);
    case TicketSalesCampaignStatus.ENDED:
      return when(campaign.endsAt, 'Ended', now);
    case TicketSalesCampaignStatus.DISABLED:
      return 'Hidden from customers';
  }
}
